import React from 'react';
import type {TabsProps as MaterialTabsProps} from '@mui/material/Tabs';
import MaterialTabs from '@mui/material/Tabs';
import {styled} from '@mui/material/styles';
import {useTabsContext} from '../Tabs/TabsContext';
import {makeTabId, makeTabPanelId} from '../Tabs/Tabs';
import {getValidChildren} from '../helpers/getValidChildren';
import {excludeComponentProps} from '../utils/excludeComponentProps';

type ModifiedTabListProps = Omit<MaterialTabsProps, 'onChange' | 'disableFocusRipple' | 'centerRipple' | 'disableRipple' | 'disableTouchRipple' | 'focusRipple' | 'onFocusVisible' | 'TouchRippleProps' | 'indicatorColor' | 'textColor' | 'value'>;

export interface TabListProps extends ModifiedTabListProps {
    /**
     * Variant of Tabs
     * @ignore
     */
    tabVariant?: 'container' | 'container-outlined';
}

// MuiButtonBase-root MuiTabScrollButton-root MuiTabScrollButton-horizontal MuiTabs-scrollButtons MuiTabs-scrollButtonsHideMobile css-145v6pe-MuiButtonBase-root-MuiTabScrollButton-root

const StyledTabs = styled(MaterialTabs, {
    ...excludeComponentProps('tabVariant')
})<TabListProps>(({
    theme, tabVariant
}) => ({
    minHeight: 'auto',
    boxShadow: `inset 0px -1px 0 0px ${theme.tokens.FuelColorBorderLight}`,
    position: 'relative',

    '& .MuiTabScrollButton-root': {
        backgroundColor: '#fffffff5',
        position: 'absolute',
        zIndex: 50,
        height: '100%',
        transitionDuration: '200ms',
        opacity: 1,
        border: `solid 1px ${theme.tokens.FuelColorBorderLight}`,
        borderBottomColor: theme.tokens.FuelColorBorderBase,

        '&.Mui-disabled': {
            opacity: 0
        },

        '&:first-of-type': {
            left: 0,
            top: 0
        },

        '&:last-of-type': {
            right: 0,
            top: 0
        }
    },

    '& .MuiTabs-indicator': {
        height: '.1875rem',
        transition: 'none',
        display: 'none',
        ...(tabVariant === 'container' || tabVariant === 'container-outlined') && {
            top: '0',
            bottom: 'auto'
        }
    },
    ...(tabVariant === 'container' || tabVariant === 'container-outlined') && {
        marginLeft: '-.0625rem',
        marginRight: '-.0625rem',
        boxShadow: 'none'
    },
    ...tabVariant === 'container-outlined' && {
        marginLeft: '0',
        marginRight: '0'
    }
}));

export const TabList = React.forwardRef<any, TabListProps>(({
    children: childrenProp, variant = 'scrollable', scrollButtons = 'auto',
    selectionFollowsFocus = true, ...props
}, ref) => {
    const {
        tabsId,
        value,
        size,
        variant: tabVariant,
        handleChange
    } = useTabsContext('TabList');

    const validChildren = getValidChildren(childrenProp);

    const children = validChildren.map((child, index) => {
        return React.cloneElement(child, {
            id: makeTabId(tabsId, index),
            size,
            variant: tabVariant,
            'aria-controls': makeTabPanelId(tabsId, index)
        });
    });

    return (
        <StyledTabs
            className='TabList-root'
            onChange={handleChange}
            ref={ref}
            scrollButtons={scrollButtons}
            selectionFollowsFocus={selectionFollowsFocus}
            tabVariant={tabVariant}
            value={value}
            variant={variant}
            {...props}
        >
            {children}
        </StyledTabs>
    );
});

TabList.displayName = 'TabList';
